import React, { memo, useCallback } from 'react'

import { TagComponent as Tag } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import Modal from 'Pods/Shared/Modal/Modal'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { STUDIO_ACCESS_TIER } from 'Pods/ThreeDStudio/Store/constants'

import useBreakpoint from 'Utils/useBreakpoint'

import EligibleForTrialBody from './Components/EligibleForTrialBody/EligibleForTrialBody'
import FreeBetaBody from './Components/FreeBetaBody/FreeBetaBody'
import HasCreditsBody from './Components/HasCreditsBody/HasCreditsBody'
import JoinWaitlistBody from './Components/JoinWaitlistBody/JoinWaitlistBody'
import SubscribedBody from './Components/SubscribedBody/SubscribedBody'
import UpgradeBody from './Components/UpgradeBody/UpgradeBody'
import WaitlistedBody from './Components/WaitlistedBody/WaitlistedBody'

const renderTag = (studioAccessTier) => {
  switch (studioAccessTier) {
    case STUDIO_ACCESS_TIER.IN_FREE_BETA:
      return <Tag className="usage-modal__tag">Free plan</Tag>
    case STUDIO_ACCESS_TIER.SUBSCRIBED:
      return <Tag color="primary-24" className="usage-modal__tag">CGTrader Subscription</Tag>
    default:
      return null
  }
}

function UsageModal() {
  const isOpen = useThreeDStudioStore(state => state.ui.isUsageModalOpen)
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs'
  const isMediumMobile = ['xs', 'sm'].includes(breakpoint)

  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const monthlyUsed = useThreeDStudioStore(state => state.subscription.monthlyGenerationsUsed)
  const monthlyAvailable = useThreeDStudioStore(state => state.subscription.monthlyGenerationsAvailable)
  const availableCredits = useThreeDStudioStore(state => state.generation.availableCredits) || 0

  // A drained trial can coexist with credits granted outside it (admin,
  // top-up); those users can still generate, so they get the credits view
  // instead of the exhausted-trial warning and its full progress bar.
  const isTrialDrainedWithCredits = monthlyAvailable > 0
    && monthlyUsed >= monthlyAvailable
    && availableCredits > 0

  const handleClose = useCallback(() => {
    closeUsageModal()
  }, [])

  if (!isOpen || !studioAccessTier) {
    return null
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMobile ? 'small' : 'medium'}
        header="Unlock AI Generation"
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <EligibleForTrialBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.MUST_JOIN_WAITLIST) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMediumMobile ? 'small' : 'medium'}
        header="Unlock AI Generation"
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <JoinWaitlistBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.WAITLISTED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header="Beta access requested"
        customClass="usage-modal usage-modal--waitlisted"
        ariaLabel="Beta access requested"
      >
        <WaitlistedBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.NOT_SUBSCRIBED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header="Unlock AI Generation"
        customClass="usage-modal"
        ariaLabel="Unlock generation"
      >
        <UpgradeBody onClose={handleClose} />
      </Modal>
    )
  }

  if (
    studioAccessTier !== STUDIO_ACCESS_TIER.IN_FREE_BETA
    && studioAccessTier !== STUDIO_ACCESS_TIER.HAS_CREDITS
    && studioAccessTier !== STUDIO_ACCESS_TIER.SUBSCRIBED
  ) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="small"
      header="CGTrader AI Usage"
      headerAddon={renderTag(studioAccessTier)}
      customClass="usage-modal"
      ariaLabel="Usage"
    >
      {studioAccessTier === STUDIO_ACCESS_TIER.IN_FREE_BETA && (
        isTrialDrainedWithCredits
          ? <HasCreditsBody onClose={handleClose} />
          : <FreeBetaBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.HAS_CREDITS && (
        <HasCreditsBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.SUBSCRIBED && (
        <SubscribedBody onClose={handleClose} />
      )}
    </Modal>
  )
}

export default memo(UsageModal)
